<template>
  <div class="flex justify-center">
    <form
      v-editable="blok"
      class="my-3 max-w-3xl"
      @submit.prevent="handleSubmit"
      @keydown.enter="handleSubmit"
    >
      <div v-if="!isFinland" class="mb-6">
        <SalutationSelect
          v-model="state.title"
          input-id="catalog_order_form_salutation"
          data-test-id="salutation_select"
        />
      </div>

      <div class="grid-cols-2 gap-4 sm:grid">
        <ValidatedInputGroupLegacy
          v-slot="{ inputId }"
          :is-valid="isFirstNameValid"
          :message="$t('newsletter_subscribe.first_name.error')"
        >
          <TextInput
            v-model="state.firstName"
            :appearance="isFirstNameValid ? 'default' : 'danger'"
            :label="$t('newsletter_subscribe.first_name.label')"
            :has-error-message="!isFirstNameValid"
            :input-id="inputId"
            :required="true"
            data-test-id="first_name"
            @change="v.firstName.$touch()"
            @blur="v.firstName.$touch()"
          />
        </ValidatedInputGroupLegacy>
        <ValidatedInputGroupLegacy
          v-slot="{ inputId }"
          :is-valid="isLastNameValid"
          :message="$t('newsletter_subscribe.last_name.error')"
        >
          <TextInput
            v-model="state.lastName"
            :appearance="isLastNameValid ? 'default' : 'danger'"
            :label="$t('newsletter_subscribe.last_name.label')"
            :has-error-message="!isLastNameValid"
            :input-id="inputId"
            :required="true"
            data-test-id="last_name"
            @change="v.lastName.$touch()"
            @blur="v.lastName.$touch()"
          />
        </ValidatedInputGroupLegacy>
      </div>

      <ValidatedInputGroupLegacy
        v-slot="{ inputId }"
        :is-valid="isEmailValid"
        :message="$t('newsletter_subscribe.email.error')"
      >
        <TextInput
          v-model="state.email"
          :appearance="isEmailValid ? 'default' : 'danger'"
          :label="$t('newsletter_subscribe.email.label')"
          :has-error-message="!isEmailValid"
          :input-id="inputId"
          :required="true"
          data-test-id="email"
          @change="v.email.$touch()"
          @blur="v.email.$touch()"
        />
      </ValidatedInputGroupLegacy>

      <ValidatedInputGroup
        v-slot="{ isValid, inputId }"
        :errors="
          !isBirthDateFocused && v.birth_date.$dirty ? v.birth_date.$errors : []
        "
      >
        <TextInput
          v-model="state.birth_date"
          autocomplete="birthdate"
          :label="$t('newsletter_subscribe.birthdate.label')"
          :placeholder="dobFormat.placeholder"
          :has-error-message="!isValid"
          :input-id="inputId"
          :mask="dobFormat.mask"
          data-test-id="birth_date"
          @focus="isBirthDateFocused = true"
          @blur="handleBlur"
        />
      </ValidatedInputGroup>

      <p class="mt-4 text-xs text-gray-secondary">
        {{ $t('form_fields.mandatory_field_note') }}
      </p>

      <div class="my-5">
        <i18n-t keypath="newsletter_subscribe.privacyPolicy.warning" tag="p">
          <template #policy>
            <NuxtLink
              :to="toLocalePath('/c/sicherheit-datenschutz')"
              hover="underline"
              size="base"
              class="text-gold-contrastful underline"
              in-paragraph
            >
              {{ $t('newsletter_subscribe.privacyPolicy.policy') }}
            </NuxtLink>
          </template>
          <template #unregister>
            <NuxtLink
              :to="toLocalePath('/c/service/newsletter/unsubscribe')"
              hover="underline"
              size="base"
              class="text-gold-contrastful underline"
              in-paragraph
            >
              {{ $t('newsletter_subscribe.privacyPolicy.unregister') }}
            </NuxtLink>
          </template>
        </i18n-t>
      </div>

      <div v-if="isDutchSpeaking" class="mt-3">
        <Checkbox
          id="newsletter-check"
          :checked="state.newsletter"
          :label="$t('newsletter_subscribe.newsletter') + ' *'"
          class="mb-4 mr-2"
          :class="{ 'text-red': !isNewsletterValid }"
          @change="state.newsletter = !state.newsletter"
        />

        <Checkbox
          id="privacyPolicy-check"
          :checked="state.privacyPolicy"
          class="mb-4 mr-2"
          :class="{ 'text-red': !isPrivacyPolicyValid }"
          @change="state.privacyPolicy = !state.privacyPolicy"
        >
          <template #label>
            <span>
              {{ $t('newsletter_subscribe.privacyPolicy.agree') }}
              <Link
                :to="toLocalePath('/c/sicherheit-datenschutz/')"
                hover="underline"
                size="sm"
                class="text-gold-contrastful underline"
                in-paragraph
              >
                {{ $t('newsletter_subscribe.privacyPolicy.link') }}
              </Link>
              {{ $t('newsletter_subscribe.privacyPolicy.agree_end').trim() }} *
            </span>
          </template>
        </Checkbox>
      </div>

      <Error v-if="error" class="my-3">
        {{ $t('newsletter_subscribe.error') }}
      </Error>

      <Button
        type="primary"
        native-type="submit"
        :disabled="isSubmitting"
        @click="handleSubmit"
        >{{ $t('newsletter_subscribe.submit') }}</Button
      >
    </form>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from 'vue'
import type { PropType } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { helpers, required } from '@vuelidate/validators'
import { routeList } from '~/utils/route'
import useRouteHelpers from '#layers/base/composables/useRouteHelpers'
import useTrackingEvents from '~/composables/tracking/useTrackingEvents'
import useNewsletter from '#layers/base/composables/useNewsletter'
import useCountryCheck from '#layers/base/composables/useCountryCheck'
import type { SbNewsletterSubscriptionNew } from '~/cms/storyblok/types/storyblok.d.ts'
import { scrollToFirstInputWithError } from '#layers/base/utils/forms'
import { formatBirthdate } from '#layers/base/utils/maskedInput'

defineProps({
  blok: {
    type: Object as PropType<SbNewsletterSubscriptionNew>,
    required: true,
  },
})

const { isInList: isFinland } = useCountryCheck(['fi'])
const { isInList: isDutchSpeaking } = useCountryCheck(['nl', 'be'])
const { subscribe, error } = useNewsletter()
const { trackNewsletterSubmit } = useTrackingEvents()
const { toLocalePath, localizedNavigateTo } = useRouteHelpers()
const { $validation, $i18n } = useNuxtApp()
const { locale } = useCurrentShop().value

const dobFormat = computed<typeof dateOfBirthFormats.de>(
  () => (dateOfBirthFormats as any)[locale],
)

const isBirthDateFocused = ref(false)
const isSubmitting = ref(false)

const isValidTitle = (value: string) =>
  isFinland.value || ['f', 'm'].includes(value)
const isValidPrivacyPolicy = (value: boolean) => value
const isValidNewsletter = (value: boolean) => value

const isValidBirthdate = (value?: string) => {
  if (!value) {
    return true
  }
  return validateOneOrMoreMasks(value, dobFormat.value.mask)
}

const state = reactive({
  email: '',
  firstName: '',
  lastName: '',
  birth_date: '',
  newsletter: !isDutchSpeaking.value,
  privacyPolicy: !isDutchSpeaking.value,
  title: isFinland.value ? undefined : ('f' as Gender),
})

const rules = {
  firstName: { required },
  lastName: { required },
  email: { required, isValidEmail },
  birth_date: {
    isValidBirthdate: helpers.withMessage(
      `${$i18n.t('myaccount.user.birthDate_error')} ${
        dobFormat.value.placeholder
      }`,
      isValidBirthdate,
    ),
    date: $validation.rule.date,
    futureDate: $validation.rule.futureDate,
  },
  title: { isValidTitle },
  privacyPolicy: { required, isValidPrivacyPolicy },
  newsletter: { required, isValidNewsletter },
}

const v = useVuelidate(rules, state, { $lazy: true })

const handleRedirect = async () => {
  await localizedNavigateTo(routeList.newsletterSuccess.path)
}

const handleSubmit = async () => {
  if (isSubmitting.value) {
    return
  }

  await v.value.$validate()

  if (!v.value.$invalid) {
    isSubmitting.value = true

    try {
      await subscribe({
        title: state.title?.length ? state.title : undefined,
        firstName: state.firstName,
        lastName: state.lastName,
        email: state.email,
        birthday: formatBirthdate(state.birth_date, dobFormat.value.mask),
        consent: {
          newsletter: state.newsletter,
          privacyPolicy: state.privacyPolicy,
        },
      })

      // Only track after successful subscription
      trackNewsletterSubmit(state.email)

      await handleRedirect()
    } catch (error) {
      console.error('Newsletter subscription failed:', error)
      isSubmitting.value = false
    }
  } else {
    scrollToFirstInputWithError()
  }
}

const handleBlur = () => {
  isBirthDateFocused.value = false
  v.value.birth_date.$touch()
}

const isEmailValid = computed(() => !v.value.email.$invalid)
const isFirstNameValid = computed(() => !v.value.firstName.$invalid)
const isLastNameValid = computed(() => !v.value.lastName.$invalid)
const isPrivacyPolicyValid = computed(() =>
  isDutchSpeaking.value ? !v.value.privacyPolicy.$invalid : true,
)
const isNewsletterValid = computed(() =>
  isDutchSpeaking.value ? !v.value.newsletter.$invalid : true,
)
</script>
